import { ReactNode, useEffect, useState } from "react";
import { useFormik } from "formik";
import ModalComponent from "..";
import {
  useCreateCustomQuestionMutation,
  useDeleteCustomQuestionMutation,
  useUpdateCustomQuestionMutation,
} from "../../../redux/features/event/event.api";
import {
  CustomQuestion,
  CustomQuestionPayload,
  CustomQuestionPlatform,
  CustomQuestionType,
  CreatedEvent,
} from "../../../redux/features/event/event.types";
import { CustomQuestionSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import Dropdown from "../../dropdown/dropdown";
import { CaretDown } from "../../icon/downcaret";
import { Icon } from "../../icon/icon";
import { SwitchInput } from "../../inputs/switch";
import { TextInput } from "../../inputs/textInput";

const platformOptions: {
  label: string;
  value: CustomQuestionPlatform;
  questionLabel: string;
  format: string;
}[] = [
  {
    label: "Instagram",
    value: "instagram",
    questionLabel: "Instagram",
    format: "handle",
  },
  { label: "X", value: "x", questionLabel: "X (Twitter)", format: "handle" },
  {
    label: "Facebook",
    value: "facebook",
    questionLabel: "Facebook",
    format: "profile",
  },
  {
    label: "TikTok",
    value: "tiktok",
    questionLabel: "TikTok",
    format: "username",
  },
  {
    label: "Snapchat",
    value: "snapchat",
    questionLabel: "Snapchat",
    format: "username",
  },
  {
    label: "LinkedIn",
    value: "linkedin",
    questionLabel: "LinkedIn",
    format: "profile",
  },
  {
    label: "YouTube",
    value: "youtube",
    questionLabel: "YouTube",
    format: "channel",
  },
  {
    label: "GitHub",
    value: "github",
    questionLabel: "GitHub",
    format: "profile",
  },
  {
    label: "Telegram",
    value: "telegram",
    questionLabel: "Telegram",
    format: "username",
  },
  {
    label: "Discord",
    value: "discord",
    questionLabel: "Discord",
    format: "username",
  },
];

const defaultPlatform = platformOptions[0].value;

const getPrefilledSocialQuestion = (platform: CustomQuestionPlatform) => {
  const meta =
    platformOptions.find((option) => option.value === platform) ??
    platformOptions[0];

  return `What is your ${meta.questionLabel} ${meta.format}?`;
};

const typeMeta: Record<
  CustomQuestionType,
  { title: string; subtitle: string; pickerTitle: string; icon: ReactNode }
> = {
  text: {
    title: "Text",
    subtitle: "Ask for a free-form response",
    pickerTitle: "Text",
    icon: <span className="text-sm font-bold">Aa</span>,
  },
  options: {
    title: "Options",
    subtitle: "Let your guests choose from a list of options",
    pickerTitle: "Options",
    icon: <Icon svg="list" width={20} height={20} />,
  },
  "social-profile": {
    title: "Social Profile",
    subtitle: "Ask for a social network username",
    pickerTitle: "Social media",
    icon: <Icon svg="circle-user" width={20} height={20} />,
  },
};

const HeaderIcon = ({ type }: { type: CustomQuestionType }) => {
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-grey-100 text-[#7183b4]">
      {typeMeta[type].icon}
    </div>
  );
};

export const CustomQuestionModal = ({
  open,
  handleClose,
  event,
  question,
  onSuccess,
}: {
  open: boolean;
  handleClose: () => void;
  event: CreatedEvent;
  question?: CustomQuestion;
  onSuccess: () => void;
}) => {
  const [selectedType, setSelectedType] = useState<
    CustomQuestionType | undefined
  >(question?.type);

  const [createQuestion, { isLoading: creating }] =
    useCreateCustomQuestionMutation();
  const [updateQuestion, { isLoading: updating }] =
    useUpdateCustomQuestionMutation();
  const [deleteQuestion, { isLoading: deleting }] =
    useDeleteCustomQuestionMutation();

  const isLoading = creating || updating || deleting;

  useEffect(() => {
    if (open) {
      setSelectedType(question?.type);
    }
  }, [open, question]);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: question?.type ?? selectedType ?? "text",
      question: question?.question ?? "",
      description: "",
      required: question?.required ?? false,
      optionType: question?.optionType ?? "single",
      optionsText: question?.options?.join("\n") ?? "Option 1",
      platform: question?.platform ?? defaultPlatform,
    },
    validationSchema: CustomQuestionSchema,
    onSubmit: async (vals) => {
      const payload: CustomQuestionPayload = {
        type: vals.type as CustomQuestionType,
        question: vals.question.trim(),
        description: "",
        required: vals.required,
        options:
          vals.type === "options"
            ? vals.optionsText
                .split("\n")
                .map((option) => option.trim())
                .filter(Boolean)
            : undefined,
        optionType:
          vals.type === "options"
            ? (vals.optionType as CustomQuestionPayload["optionType"])
            : undefined,
        platform:
          vals.type === "social-profile"
            ? (vals.platform as CustomQuestionPlatform)
            : undefined,
      };

      const resp = question
        ? await updateQuestion({
            eventId: event._id,
            questionId: question._id,
            payload,
          })
        : await createQuestion({
            eventId: event._id,
            payload,
          });

      if (resp.data?.status === "success") {
        resetAndClose();
        onSuccess();
      }
    },
  });

  const resetAndClose = () => {
    resetForm();
    setSelectedType(question?.type);
    handleClose();
  };

  const chooseType = (type: CustomQuestionType) => {
    setSelectedType(type);
    setFieldValue("type", type);

    if (type === "options" && !values.optionsText) {
      setFieldValue("optionsText", "Option 1");
    }
  };

  const options = values.optionsText ? values.optionsText.split("\n") : [];

  const updateOption = (idx: number, value: string) => {
    const nextOptions = [...options];
    nextOptions[idx] = value;
    setFieldValue("optionsText", nextOptions.join("\n"));
  };

  const removeOption = (idx: number) => {
    const nextOptions = options.filter((_, optionIdx) => optionIdx !== idx);
    setFieldValue("optionsText", nextOptions.join("\n"));
  };

  const addOption = () => {
    setFieldValue(
      "optionsText",
      [...options, `Option ${options.length + 1}`].join("\n"),
    );
  };

  const handleDeleteQuestion = async () => {
    if (!question) {
      return;
    }

    if (
      !window.confirm(
        "Delete this checkout question? Existing answers stay on previous guests.",
      )
    ) {
      return;
    }

    const resp = await deleteQuestion({
      eventId: event._id,
      questionId: question._id,
    });

    if (resp.data?.status === "success") {
      resetAndClose();
      onSuccess();
    }
  };

  const currentType = (selectedType ?? values.type) as CustomQuestionType;

  useEffect(() => {
    if (currentType !== "social-profile") {
      return;
    }

    const currentQuestion = values.question.trim();
    const isDefaultQuestion =
      !currentQuestion ||
      platformOptions.some(
        (option) =>
          currentQuestion === getPrefilledSocialQuestion(option.value),
      );

    if (!isDefaultQuestion) {
      return;
    }

    const prefilled = getPrefilledSocialQuestion(
      values.platform as CustomQuestionPlatform,
    );

    if (currentQuestion !== prefilled) {
      setFieldValue("question", prefilled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentType, values.platform]);

  if (!question && !selectedType) {
    return (
      <ModalComponent
        open={open}
        handleClose={resetAndClose}
        showHeader={false}
        mobileAnchorBottom
        closeOnOverlayClick
        className="md:!max-w-[620px]"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-lg font-bold text-grey-950">
              Add a custom question
            </p>
            <button
              type="button"
              aria-label="Close custom question modal"
              className="text-grey-950"
              onClick={resetAndClose}
            >
              <Icon svg="close-icon" width={22} height={22} />
            </button>
          </div>

          <div className="flex flex-col gap-3">
            {(
              ["text", "options", "social-profile"] as CustomQuestionType[]
            ).map((type) => (
              <button
                key={type}
                type="button"
                className="flex items-center gap-4 rounded-xl bg-grey-50 px-4 py-3 text-left font-bold text-grey-950"
                onClick={() => chooseType(type)}
              >
                <div className="flex w-6 justify-center text-[#7183b4]">
                  {typeMeta[type].icon}
                </div>
                <span className="flex-1">{typeMeta[type].pickerTitle}</span>
                <Icon svg="chevron-right" width={20} height={20} />
              </button>
            ))}
          </div>
        </div>
      </ModalComponent>
    );
  }

  return (
    <ModalComponent
      open={open}
      handleClose={resetAndClose}
      showHeader={false}
      mobileAnchorBottom
      closeOnOverlayClick
      className="md:!max-w-[620px]"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <HeaderIcon type={currentType} />
            <div>
              <p className="text-lg font-bold text-grey-950">
                {typeMeta[currentType].title}
              </p>
              <p className="text-sm text-grey-700">
                {typeMeta[currentType].subtitle}
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close custom question modal"
            className="text-grey-950"
            onClick={resetAndClose}
          >
            <Icon svg="close-icon" width={22} height={22} />
          </button>
        </div>

        {currentType === "options" && (
          <div className="grid grid-cols-2 rounded-lg bg-grey-50 p-1">
            {[
              { label: "Single selection", value: "single" },
              { label: "Multiple selection", value: "multiple" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                  values.optionType === option.value
                    ? "bg-white text-grey-950 shadow-sm"
                    : "text-primary"
                }`}
                onClick={() => {
                  setFieldValue("optionType", option.value);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {currentType === "social-profile" && (
          <Dropdown
            menuItems={platformOptions.map((option) => ({
              name: option.label,
              value: option.value,
              onClick: () => {
                setFieldValue("platform", option.value);
              },
            }))}
            menuClassName="!z-[999]"
            menuExactWidth
            position="bottom start"
          >
            <button type="button" className="w-full text-left">
              <TextInput
                type="text"
                name="platform"
                value={
                  platformOptions.find(
                    (option) => option.value === values.platform,
                  )?.label ?? ""
                }
                placeholder="Select platform"
                onChange={() => {}}
                onBlur={handleBlur}
                hasTitle
                title="Platform"
                readOnly
                customIcon={<CaretDown />}
                customIconPosition="right"
                errors={
                  touched.platform && errors.platform
                    ? errors.platform
                    : undefined
                }
              />
            </button>
          </Dropdown>
        )}

        <TextInput
          type="text"
          name="question"
          value={values.question}
          placeholder={
            currentType === "social-profile"
              ? getPrefilledSocialQuestion(
                  values.platform as CustomQuestionPlatform,
                )
              : "Enter question"
          }
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Question"
          errors={touched.question && errors.question ? errors.question : ""}
        />

        {currentType === "options" && (
          <div className="flex flex-col gap-3">
            <p className="text-base font-bold text-grey-900">Options</p>

            {options.map((option, idx) => (
              <div className="flex items-start gap-2" key={`${idx}-option`}>
                <TextInput
                  type="text"
                  name={`custom-question-option-${idx}`}
                  value={option}
                  placeholder={`Enter option ${idx + 1}`}
                  onChange={(event) => {
                    updateOption(idx, event.target.value);
                  }}
                  onBlur={handleBlur}
                  hasTitle
                  title="Option"
                  containerClassName="flex-1"
                />

                {options.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove option ${idx + 1}`}
                    className="mt-2 rounded-full border border-grey-100 p-3 text-grey-500"
                    onClick={() => removeOption(idx)}
                  >
                    <Icon svg="delete" width={16} height={16} />
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="flex w-fit items-center gap-1 text-sm font-bold text-blue"
              onClick={addOption}
            >
              <Icon svg="plus" width={18} height={18} />
              Add option
            </button>

            {touched.optionsText && errors.optionsText ? (
              <p className="text-xs text-danger">{errors.optionsText}</p>
            ) : undefined}
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-base font-bold text-grey-900">Required</p>
          <SwitchInput
            checked={values.required}
            onChange={(checked) => {
              setFieldValue("required", !checked);
            }}
          />
        </div>

        <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-end">
          {!question && (
            <Button
              type="button"
              text="Back"
              variation="plain-grey"
              className="md:max-w-[112px]"
              disabled={isLoading}
              onClick={() => setSelectedType(undefined)}
            />
          )}

          {question && (
            <Button
              type="button"
              text="Delete"
              variation="plain-danger"
              className="md:max-w-[112px]"
              disabled={isLoading}
              loading={deleting}
              onClick={handleDeleteQuestion}
            />
          )}

          <Button
            type="submit"
            text={question ? "Save Changes" : "Add Question"}
            loading={isLoading}
            disabled={isLoading}
            className="md:max-w-[160px]"
          />
        </div>
      </form>
    </ModalComponent>
  );
};
